/* eslint-disable react/prop-types */
import {
  Box,
  Button,
  Dialog,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import { motion } from "framer-motion"; // Import motion
import { DefaultLayoutZIndex } from "../../_utils/constraints";
import LoadingBox from "./LoadingBox";
import ErrorBox from "./ErrorBox";
import ImageCard from "./_card/ImageCard";

const DialogBox = ({
  open,
  handleClose,
  onHandle,
  loading,
  error,
  alt,
  src,
  title,
  heading,
  description,
  yesButtonLabel,
  loadingText,
  imgHeight,
  imgWidth,
}) => {
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("md"));

  // Define motion variants
  const variants = {
    hidden: { opacity: 0, y: -20 }, // Start hidden
    visible: { opacity: 1, y: 0 },   // End state
    exit: { opacity: 0, y: 20 },     // Exit animation
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby={`${heading}-${alt}-${src}`}
      aria-labelledby={`${heading}-${alt}-${src}-3`}
      style={{ ...(open && { zIndex: DefaultLayoutZIndex + 20 }),backgroundColor:"transparent" }}

    >
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={variants}
        transition={{ duration: 0.3 }} // Duration of the animation
      >
        <Box sx={{ p: 2, position: "relative", width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between", // Ensures the title is centered and the close button is on the right
            }}
          >
            <Box sx={{ flex: 1, textAlign: "center" }}>
              {title ? (
                <Typography
                  variant="h5"
                  component="h5"
                  fontWeight="bold"
                  color="secondary"
                >
                  {title}
                </Typography>
              ) : null}
            </Box>

            <Tooltip
              title="Closed"
              placement="bottom-start"
              onClick={handleClose}
              PopperProps={{
                modifiers: [
                  {
                    name: "zIndex",
                    enabled: true,
                    phase: "beforeWrite",
                    fn: ({ state }) => {
                      state.styles.popper.zIndex = DefaultLayoutZIndex + 24; // Customize your zIndex value here
                    },
                  },
                ],
              }}
            >
              <CloseIcon color="error" fontSize="large" />
            </Tooltip>
          </Box>

          <Box sx={{ position: "relative", width: "100%" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              {!loading && src ? (
                <ImageCard
                  alt={alt}
                  src={src}
                  imgWidth={imgWidth ? imgWidth : 200}
                  imgHeight={imgHeight ? imgHeight : 200}
                />
              ) : null}

              <Box
                sx={{
                  width: "100%",
                  ...(src && { mt: 2 }),
                  textAlign: "center",
                }}
              >
                {heading ? (
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="dark"
                    marginBottom="2px"
                  >
                    {heading}
                  </Typography>
                ) : null}

                {typeof description === "string" ? (
                  <Typography variant="body2" fontWeight="regular" color="text">
                    {description}
                  </Typography>
                ) : React.isValidElement(description) ? (
                  <Box sx={{ width: "100%" }}>{description}</Box>
                ) : null}
              </Box>

              <LoadingBox
                isLoading={loading}
                text={loadingText}
                isFullScreen={false}
                styleLoadingBox={{ pt: 1 }}
              />

              <ErrorBox
                isError={error}
                error={error}
                isFullScreen={false}
                styleErrorBox={{}}
              />
            </Box>
          </Box>
          {onHandle ? (
            <Box
              sx={{
                my: 2,
                width: "100%",
                display: "flex",
                justifyContent: "center",
                flexDirection: isMobileView ? "column" : "row",
                alignItems: "center",
              }}
            >
              <Button
                onClick={handleClose}
                variant="outlined"
                color="info"
                type="button"
                size="medium"
                sx={{ m: 0, ...(isMobileView && { width: "100%" }) }}
              >
                No
              </Button>
              {yesButtonLabel ? (
                <Button
                  autoFocus
                  variant="gradient"
                  color="error"
                  onClick={onHandle}
                  size="medium"
                  sx={{
                    m: 0,
                    ...(isMobileView ? { width: "100%", mt: 1 } : { ml: 1 }),
                  }}
                >
                  {yesButtonLabel}
                </Button>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </motion.div>
    </Dialog>
  );
};

export default DialogBox;
